import { AppLayout } from '@layouts/app-layout/AppLayout';
import { AvailabilityManagerPage } from '@pages/availability-manager/AvailabilityManagerPage';
import { CaeDocumentsPage } from '@pages/documents/cae/CaeDocumentsPage';
import { CompaniesDocumentsPage } from '@pages/documents/companies/CompaniesDocumentsPage';
import { DocumentsPage } from '@pages/documents/DocumentsPage';
import { InstructCustomerDocumentsRouterPage } from '@pages/instruct-customer-documents/InstructCustomerDocumentsRouterPage';
import { InstructRedirectPage } from '@pages/instruct-redirect/InstructRedirectPage';
import { ResourceLookupPage } from '@pages/resource-lookup/ResourceLookupPage';
import { ScheduleRouterPage } from '@pages/schedule/ScheduleRouterPage';
import { ErrorPage } from '@shared/components/error-pages/ErrorPage';
import { AuthenticatedRoute } from '@shared/components/route-guards/authenticated-route/AuthenticatedRoute';
import { FeatureFlagGuard } from '@shared/components/route-guards/feature-flag-guard/FeatureFlagGuard';
import { ModuleGuard } from '@shared/components/route-guards/module-guard/ModuleGuard';
import { PermissionGuard } from '@shared/components/route-guards/permission-guard/PermissionGuard';
import { Navigate, type RouteObject } from 'react-router-dom';

/**
 * Instruct module routes - for Instructors
 * Base path: /instruct
 *
 * Guard stack:
 * 1. AuthenticatedRoute - User must be logged in
 * 2. ModuleGuard - User must have Instruct module access
 */
export const instructRoutes: RouteObject[] = [
  {
    path: '/instruct',
    element: (
      <AuthenticatedRoute>
        <ModuleGuard />
      </AuthenticatedRoute>
    ),
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            index: true,
            element: <InstructRedirectPage />,
          },          
          {
            path: 'schedule',
            children: [
              {
                path: 'my-schedule/*',
                element: (
                  <FeatureFlagGuard flag="instructormyschedule">
                    <PermissionGuard permission="InstructorSchedule_View_Own">
                      <ScheduleRouterPage />
                    </PermissionGuard>
                  </FeatureFlagGuard>
                ),
              },
              {               
                path: 'availability-manager/*',
                element: (
                  <FeatureFlagGuard flag="instructorExpAvailabilityManager">
                    <PermissionGuard permission="AvailabilityManager_FullAccess_Own">
                      <AvailabilityManagerPage />
                    </PermissionGuard>
                  </FeatureFlagGuard>
                ),
              },
            ],
          },       
          {
            path: 'resource-lookup',
            element: (
              <FeatureFlagGuard flag="instructorExpResourceLookup">
                <PermissionGuard permission={['Resources_View_MyLocations']}>
                  <ResourceLookupPage />
                </PermissionGuard>
              </FeatureFlagGuard>
            ),
          },
          {
            path: 'documents',
            element: (
              <PermissionGuard
                permission={[
                  'CustomerTrainingDocuments_View_All',
                  'CAETrainingDocuments_View_All',
                ]}
                operator="some"
              >
                <DocumentsPage />
              </PermissionGuard>
            ),
            children: [
              { index: true, element: <Navigate to="companies" replace /> },
              {
                path: 'companies',
                element: (
                  <PermissionGuard permission="CustomerTrainingDocuments_View_All">
                    <CompaniesDocumentsPage />
                  </PermissionGuard>
                ),
              },
              {
                path: 'customer-documents/*',
                element: (
                  <FeatureFlagGuard flag="instructorViewCustomerNonFaaDocuments">
                    <PermissionGuard permission="CustomerTrainingDocuments_View_All">
                      <InstructCustomerDocumentsRouterPage />
                    </PermissionGuard>
                  </FeatureFlagGuard>
                ),
              },
              {
                path: 'cae',
                element: (
                  <PermissionGuard permission="CAETrainingDocuments_View_All">
                    <CaeDocumentsPage />
                  </PermissionGuard>
                ),
              },
            ],
          },
          {
            path: '*',
            element: <ErrorPage type="not-found" />,
          },
        ],
      },
    ],
  },
];
